import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { uuidv4 } from "./helpers";
import type { LoadingActions } from "components/StoreContextWrapper/actions";

interface RequestOptions<T> extends Partial<LoadingActions> {
    onResult(value: T): void;
    onError(error: unknown): void;
}

/** Own one request stream. Superseded and unmounted requests cannot publish results. */
export const useLatestRequest = <T>(options: RequestOptions<T>) => {
    const latest = useRef(options);
    latest.current = options;
    const current = useRef<AbortController | null>(null);
    const mounted = useRef(true);
    const [requestId] = useState(uuidv4);
    const [isPending, setIsPending] = useState(false);

    const cancel = useCallback(() => {
        const controller = current.current;
        current.current = null;
        if (controller) {
            controller.abort();
            latest.current.stopLoading?.(requestId);
            if (mounted.current) setIsPending(false);
        }
    }, [requestId]);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            cancel();
        };
    }, [cancel]);

    const run = useCallback(
        async (request: (signal: AbortSignal) => Promise<T>) => {
            if (!mounted.current) return;
            const previous = current.current;
            previous?.abort();
            const controller = new AbortController();
            current.current = controller;
            if (!previous) latest.current.startLoading?.(requestId);
            setIsPending(true);
            try {
                const result = await request(controller.signal);
                if (
                    mounted.current &&
                    current.current === controller &&
                    !controller.signal.aborted
                )
                    latest.current.onResult(result);
            } catch (error) {
                if (
                    mounted.current &&
                    current.current === controller &&
                    !controller.signal.aborted
                )
                    latest.current.onError(error);
            } finally {
                if (current.current === controller) {
                    current.current = null;
                    latest.current.stopLoading?.(requestId);
                    if (mounted.current) setIsPending(false);
                }
            }
        },
        [requestId],
    );

    return { run, cancel, isPending };
};
